'use client';

import { useCallback, useState } from 'react';
import Image from 'next/image';

import DoneIcon from '@public/icons/Done_round.svg';
import { Button } from '@/ui/button/button';
import { Modal } from '@/ui/modal/modal';
import { CartItem, getItemsQuantity, useCartItems } from '@/app/cartContext';
import { ClientProduct } from '@/api/types/products';
import { ModalContent } from '@/components/modal-content/modal-content';

import styles from './product-card.module.css';
import { ImageWithStub } from '@/ui/image-with-stub/image-with-stub';
import { Drawer } from 'antd';
import { useInfo } from '@/app/infoContext';

type ProductCardProps = {
  product: ClientProduct;
  isAvailable?: boolean;
};

export const ProductCard = ({ product, isAvailable }: ProductCardProps) => {
  const { name, price, description, weight, id, imageUrl } = product;
  const { isMobile } = useInfo();

  const { addItem, findItemAllById } = useCartItems();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const handleAddToCart = (product: CartItem) => {
    addItem(product);
    handleCloseModal();
  };

  const cartItemsCount = getItemsQuantity(findItemAllById(product.id));

  return (
    <>
      <div
        className={`${styles.productCard} ${isMobile ? styles.isMobile : ''}`}
        onClick={isAvailable ? handleOpenModal : undefined}
        role="button"
        tabIndex={0}
      >
        <div className={styles.main}>
          {!!cartItemsCount && (
            <div className={styles.imageWrapper}>
              <ImageWithStub
                src={imageUrl}
                alt={`${name} - фото товара`}
                width={241}
                height={266}
                className={`${styles.image} ${isMobile ? styles.isMobile : ''}`}
              />
              <div className={styles.overlay}>
                <p className={styles.overlayText}>{cartItemsCount}</p>
              </div>
            </div>
          )}
          {!cartItemsCount && (
            <ImageWithStub
              src={imageUrl}
              alt={`${name} - фото товара`}
              width={241}
              height={266}
              className={`${styles.image} ${isMobile ? styles.isMobile : ''}`}
            />
          )}
          <div className={styles.name}>{name}</div>
          <div className={styles.description}>{description}</div>
          <div className={styles.weight}>Масса: {weight} гр.</div>
        </div>
        <div className={styles.footer}>
          <div className={styles.price}>{price} ₽</div>
          {isAvailable && (
            <Button
              type={cartItemsCount ? 'secondary' : 'primary'}
              aria-label={`Добавить ${name} в корзину`}
              as={'div'}
            >
              {cartItemsCount ? (
                <div className={styles.buttonSuccess}>
                  <Image src={DoneIcon} alt={'Success'} className={styles.doneIcon} />
                  <div>В корзине</div>
                </div>
              ) : (
                'В корзину'
              )}
            </Button>
          )}
        </div>
      </div>
      {isMobile ?
        <Drawer open={isModalOpen} onClose={handleCloseModal} size='large'>
          <ModalContent id={id} onAccept={handleAddToCart} isMobile={true} />
        </Drawer>
        : <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <ModalContent id={id} onAccept={handleAddToCart} />
        </Modal>}
    </>
  );
};
